import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { api, apiFile } from "../api";
import { InvoicePreviewModal } from "../components/InvoicePreviewModal";
import { formatAccountCode, formatMoney } from "../formatting";
type Entry = { id: number; account_code: string; date: string; document_no: string; voucher_no: string; debit: string; credit: string; description: string; filename: string };
type Row = { status: string; invoice_id: number | null; invoice_has_artifact: boolean; invoice_no: string; invoice_vat: string | null; accounting_vat: string | null; entries: Entry[] };
type Comparison = { rows: Row[]; summary: Record<string, number>; sheets: Record<string, string> };
type Return = { id: number; invoice_no: string; lines: { id: number; name: string; quantity: string; unit: string; original_line_id: number | null }[]; candidates: { id: number; invoice_no: string; name: string; quantity: string; unit: string }[] };

export function AccountingReview({ caseId, canPrepare }: { caseId: number; canPrepare: boolean }) {
  const [returns, setReturns] = useState<Return[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [choices, setChoices] = useState<Record<number, { original_line_id: string; original_quantity: string }>>({});

  async function load() {
    const returnData = await api<{ returns: Return[] }>(`/api/refund-cases/${caseId}/purchase-returns`);
    setReturns(returnData.returns); setChoices({});
  }
  useEffect(() => {
    let active = true;
    setError(""); setReturns([]); setChoices({}); setPreviewOpen(false);
    api<{ returns: Return[] }>(`/api/refund-cases/${caseId}/purchase-returns`)
      .then(returnData => { if (active) setReturns(returnData.returns); })
      .catch(reason => { if (active) setError(String(reason)); });
    return () => { active = false; };
  }, [caseId]);

  async function saveReturn(returned: Return) {
    setBusy(true); setError("");
    try {
      await api(`/api/refund-cases/${caseId}/purchase-returns`, { method: "POST", body: JSON.stringify({ invoice_id: returned.id,
        lines: returned.lines.map(line => ({ return_line_id: line.id, original_line_id: Number(choices[line.id]?.original_line_id || line.original_line_id), original_quantity: choices[line.id]?.original_quantity })) }) });
      await load();
    } catch (reason) { setError(String(reason)); } finally { setBusy(false); }
  }
  async function downloadReport(kind: "accounting-comparison/export" | "atik-stock") {
    setBusy(true); setError("");
    try {
      const result = await apiFile(`/api/refund-cases/${caseId}/${kind}`);
      const url = URL.createObjectURL(result.blob);
      const link = document.createElement("a"); link.href = url; link.download = result.filename || (kind === "atik-stock" ? "atik-kalan-kdv.xlsx" : "fatura-191-karsilastirmasi.xlsx"); link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (reason) { setError(String(reason)); } finally { setBusy(false); }
  }
  return <><section className="panel">
    <div className="panel-head"><h2>Fatura / 191 karşılaştırması</h2><div className="accounting-report-actions">
      <button className="secondary-button" disabled={busy} onClick={() => setPreviewOpen(true)}>Fatura / 191 karşılaştırmasını önizle</button>
      <button className="secondary-button" disabled={busy} onClick={() => void downloadReport("accounting-comparison/export")}>Fatura / 191 karşılaştırmasını indir</button>
    </div></div>
    <p>Karşılaştırma sonuçları Excel’de durumlarına göre ayrı sayfalardadır. Aynı ayda e-Defter varsa, e-Defter esas alınır.</p>
    {error && <div className="issue warning" role="alert">{error}</div>}
    {returns.length > 0 && <><h3>Alıştan iadelerin asıl fatura bağlantıları</h3><p>İade miktarını seçtiğiniz asıl alış kaleminin biriminde girin. Kaydettiğinizde ilgili hesaplar yeniden hesaplama gerektirir.</p>
      {returns.map(returned => <details key={returned.id}><summary>{returned.invoice_no}</summary>
        {returned.lines.map(line => <div key={line.id} className="source-import-row"><span>{line.name} · {line.quantity} {line.unit}</span>
          <label>Asıl alış kalemi<select disabled={!canPrepare || busy} value={choices[line.id]?.original_line_id ?? String(line.original_line_id || "")} onChange={e => setChoices(current => ({ ...current, [line.id]: { original_quantity: current[line.id]?.original_quantity || "", original_line_id: e.target.value } }))}>
            <option value="">Seçin</option>{returned.candidates.map(candidate => <option value={candidate.id} key={candidate.id}>{candidate.invoice_no} · {candidate.name} · {candidate.quantity} {candidate.unit}</option>)}
          </select></label>
          <label>Asıl alış biriminde iade miktarı<input type="number" min="0.000001" step="any" disabled={!canPrepare || busy} value={choices[line.id]?.original_quantity || ""} onChange={e => setChoices(current => ({ ...current, [line.id]: { original_line_id: current[line.id]?.original_line_id || String(line.original_line_id || ""), original_quantity: e.target.value } }))} /></label>
        </div>)}
        {canPrepare && <button className="secondary-button" disabled={busy || returned.lines.some(line => !choices[line.id]?.original_quantity || !(choices[line.id]?.original_line_id || line.original_line_id))} onClick={() => void saveReturn(returned)}>İade bağlantılarını kaydet</button>}
      </details>)}
    </>}
    {previewOpen && <ComparisonPreview key={caseId} caseId={caseId} onClose={() => setPreviewOpen(false)} />}
  </section>
  <section className="panel">
    <div className="panel-head"><h2>ATİK kalan KDV raporu</h2>
      <button className="secondary-button" disabled={busy} onClick={() => void downloadReport("atik-stock")}>ATİK kalan KDV raporunu indir</button>
    </div>
  </section></>;
}

function ComparisonPreview({ caseId, onClose }: { caseId: number; onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [data, setData] = useState<Comparison | null>(null);
  const [error, setError] = useState("");
  const [sheet, setSheet] = useState("matched");
  const [page, setPage] = useState(0);
  const [invoice, setInvoice] = useState<{ id: number; invoice_no: string } | null>(null);
  const [accounting, setAccounting] = useState<{ invoice_no: string; entries: Entry[] } | null>(null);
  const pageSize = 50;
  useLayoutEffect(() => {
    const element = dialog.current!;
    const focus = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    element.showModal();
    return () => {
      element.close();
      document.body.style.overflow = overflow;
      if (focus instanceof HTMLElement && focus.isConnected) focus.focus({ preventScroll: true });
    };
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    void api<Comparison>(`/api/refund-cases/${caseId}/accounting-comparison`, { signal: controller.signal })
      .then(result => { if (!controller.signal.aborted) setData(result); })
      .catch(reason => { if (!controller.signal.aborted) setError(String(reason)); });
    return () => controller.abort();
  }, [caseId]);
  const rows = useMemo(() => data?.rows.map((row, index) => ({ ...row, recordNo: index + 1 })) || [], [data]);
  const details = useMemo(() => rows.flatMap(row => row.entries.map(entry => ({ row, entry }))), [rows]);
  const selected = rows.filter(row => row.status === sheet);
  const total = sheet === "details" ? details.length : selected.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const start = page * pageSize;
  const invoiceButton = (row: Row) => row.invoice_id && row.invoice_has_artifact ? <button type="button" className="text-button"
    aria-label={`${row.invoice_no} faturasını gör`} onClick={() => setInvoice({ id: row.invoice_id!, invoice_no: row.invoice_no })}>Gör</button> : "—";
  const accountingButton = (row: Row, entries = row.entries) => entries.length ? <button type="button" className="text-button"
    aria-label={`${row.invoice_no || "Belge numarası eksik"} muhasebe kaydını gör`} onClick={() => setAccounting({ invoice_no: row.invoice_no, entries })}>Gör</button> : "—";
  return <>
    <dialog ref={dialog} className="modal invoice-preview-modal accounting-comparison-modal" aria-label="Fatura / 191 karşılaştırması önizleme"
      onCancel={event => { event.preventDefault(); onClose(); }}>
      <div className="panel-head"><h2>Fatura / 191 karşılaştırması</h2><button type="button" className="secondary-button compact" autoFocus onClick={onClose}>Kapat</button></div>
      {error ? <p className="issue warning" role="alert">{error}</p> : !data ? <p role="status">Karşılaştırma hazırlanıyor…</p> : <>
        <div className="accounting-comparison-tabs" role="tablist" aria-label="Excel sayfaları">
          {[...Object.entries(data.sheets), ["details", "Muhasebe Detayları"]].map(([key, title]) => <button type="button" key={key}
            id={`comparison-tab-${key}`} role="tab" aria-selected={sheet === key} aria-controls="comparison-sheet"
            className={`secondary-button compact ${sheet === key ? "selected" : ""}`} onClick={() => { setSheet(key); setPage(0); }}>
            {title} ({key === "details" ? details.length : data.summary[key] || 0})
          </button>)}
        </div>
        <div className="accounting-comparison-table" id="comparison-sheet" role="tabpanel" aria-labelledby={`comparison-tab-${sheet}`}>
          <table>
            {sheet === "details" ? <>
              <thead><tr>{["Kayıt no", "Durum", "Fatura no", "Tarih", "Kaynak dosya", "Fiş no", "Belge no", "Hesap", "Borç", "Alacak", "Açıklama", "Fatura", "Muhasebe"].map(title => <th key={title}>{title}</th>)}</tr></thead>
              <tbody>{details.slice(start, start + pageSize).map(({ row, entry }) => <tr key={`${row.recordNo}-${entry.id}`}>
                <td>{row.recordNo}</td><td>{data.sheets[row.status]}</td><td>{row.invoice_no || "—"}</td>
                <td className="accounting-comparison-code">{entry.date.split("-").reverse().join(".")}</td><td>{entry.filename}</td><td>{entry.voucher_no || "—"}</td>
                <td>{entry.document_no || "—"}</td><td className="accounting-comparison-code">{formatAccountCode(entry.account_code)}</td>
                <td className="accounting-comparison-amount">{formatMoney(entry.debit)}</td><td className="accounting-comparison-amount">{formatMoney(entry.credit)}</td><td className="accounting-comparison-description">{entry.description || "—"}</td>
                <td>{invoiceButton(row)}</td><td>{accountingButton(row, [entry])}</td>
              </tr>)}</tbody>
            </> : <>
              <thead><tr>{["Kayıt no", "Fatura no", "Fatura KDV", "191 KDV", "Fark (191 - fatura)", "Fatura", "Muhasebe"].map(title => <th key={title}>{title}</th>)}</tr></thead>
              <tbody>{selected.slice(start, start + pageSize).map(row => <tr key={row.recordNo}>
                <td>{row.recordNo}</td><td>{row.invoice_no || "—"}</td>
                <td className="accounting-comparison-amount">{row.invoice_vat === null ? "—" : formatMoney(row.invoice_vat)}</td>
                <td className="accounting-comparison-amount">{row.accounting_vat === null ? "—" : formatMoney(row.accounting_vat)}</td>
                <td className="accounting-comparison-amount">{row.accounting_vat === null || row.invoice_vat === null ? "—" : formatMoney(String(Number(row.accounting_vat) - Number(row.invoice_vat)))}</td>
                <td>{invoiceButton(row)}</td><td>{accountingButton(row)}</td>
              </tr>)}</tbody>
            </>}
          </table>
          {total === 0 && <p>Bu sayfada kayıt yok.</p>}
        </div>
        <div className="accounting-comparison-pagination"><span>{total ? `${start + 1}–${Math.min(start + pageSize, total)} / ${total} kayıt` : "0 kayıt"}</span>
          <button type="button" className="secondary-button compact" disabled={page === 0} onClick={() => setPage(page - 1)}>Önceki</button>
          <span>{page + 1} / {pages}</span>
          <button type="button" className="secondary-button compact" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)}>Sonraki</button>
        </div>
      </>}
    </dialog>
    {invoice && <InvoicePreviewModal invoice={invoice} onClose={() => setInvoice(null)} />}
    {accounting && <AccountingRecordPreview record={accounting} onClose={() => setAccounting(null)} />}
  </>;
}

function AccountingRecordPreview({ record, onClose }: { record: { invoice_no: string; entries: Entry[] }; onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  useLayoutEffect(() => {
    const element = dialog.current!;
    const focus = document.activeElement;
    element.showModal();
    return () => {
      element.close();
      if (focus instanceof HTMLElement && focus.isConnected) focus.focus({ preventScroll: true });
    };
  }, []);
  return <dialog ref={dialog} className="modal invoice-preview-modal accounting-record-modal" aria-label="Muhasebe kaydı önizlemesi"
    onCancel={event => { event.preventDefault(); onClose(); }}>
    <div className="panel-head"><h2>{record.invoice_no || "Belge numarası eksik"} · Muhasebe kaydı</h2>
      <button type="button" className="secondary-button compact" autoFocus onClick={onClose}>Kapat</button></div>
    <div className="accounting-comparison-table"><table>
      <thead><tr>{["Tarih", "Kaynak dosya", "Fiş no", "Belge no", "Hesap", "Borç", "Alacak", "Açıklama"].map(title => <th key={title}>{title}</th>)}</tr></thead>
      <tbody>{record.entries.map(entry => <tr key={entry.id}>
        <td className="accounting-comparison-code">{entry.date.split("-").reverse().join(".")}</td><td>{entry.filename}</td><td>{entry.voucher_no || "—"}</td>
        <td>{entry.document_no || "—"}</td><td className="accounting-comparison-code">{formatAccountCode(entry.account_code)}</td>
        <td className="accounting-comparison-amount">{formatMoney(entry.debit)}</td><td className="accounting-comparison-amount">{formatMoney(entry.credit)}</td><td className="accounting-comparison-description">{entry.description || "—"}</td>
      </tr>)}</tbody>
    </table></div>
  </dialog>;
}
